import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { ProcessStatus } from '../types/api';
import { apiCall } from '../utils/api';

interface ProcessContextType {
  status: ProcessStatus;
  updateStatus: (newStatus: Partial<ProcessStatus>) => void;
  startProcess: (clientName: string) => Promise<void>;
}

const ProcessContext = createContext<ProcessContextType | undefined>(undefined);

interface ProcessProviderProps {
  children: ReactNode;
}

export const ProcessProvider: React.FC<ProcessProviderProps> = ({ children }) => {
  const [status, setStatus] = useState<ProcessStatus>({
    running: false,
    done: false,
    start_time: null,
    end_time: null,
    error: null,
    process_id: null,
    current_step: null,
    progress: 0,
  });

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const updateStatus = (newStatus: Partial<ProcessStatus>) => {
    setStatus(prev => ({ ...prev, ...newStatus }));
  };

  const fetchCurrentStatus = async () => {
    try {
      const response = await apiCall('/api/process/status');
      if (response.ok) {
        const statusData = await response.json();
        updateStatus(statusData);
      }
    } catch (error) {
      console.error('Error fetching current status:', error);
    }
  };

  const startPolling = () => {
    // Don't start if already polling
    if (pollingIntervalRef.current) return;

    console.log('Starting status polling (every 1 second)');
    pollingIntervalRef.current = setInterval(fetchCurrentStatus, 1000);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      console.log('Stopping status polling');
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const startProcess = async (clientName: string) => {
    try {
      console.log('Starting process for client:', clientName);

      // Optimistically update state to show starting immediately
      updateStatus({
        running: true,
        done: false,
        error: null,
        current_step: 'Starting...',
        progress: 0
      });

      const response = await apiCall('/api/process/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ client_name: clientName }),
      });

      if (!response.ok) {
        throw new Error(`Failed to start process: ${response.status}`);
      }

      const result = await response.json();
      console.log('Process start response:', result);

      // Update with process ID but keep running state
      updateStatus({
        process_id: result.process_id,
        start_time: new Date().toISOString()
      });

    } catch (error) {
      console.error('Error starting process:', error);
      updateStatus({
        running: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  };

  // Fetch initial status on mount
  useEffect(() => {
    fetchCurrentStatus();
  }, []);

  // Start/stop polling based on process state
  useEffect(() => {
    if (status.running && !status.done) {
      // Process is running, start polling
      startPolling();
    } else {
      // Process not running or completed, stop polling
      stopPolling();
    }

    // Cleanup on unmount
    return () => {
      stopPolling();
    };
  }, [status.running, status.done]);

  // Handle process completion
  useEffect(() => {
    if (status.done && status.running) {
      // Process completed, update final state
      setStatus(prev => ({
        ...prev,
        running: false,
        progress: 100,
        current_step: 'Analysis complete!'
      }));
    }
  }, [status.done, status.running]);

  return (
    <ProcessContext.Provider
      value={{
        status,
        updateStatus,
        startProcess,
      }}
    >
      {children}
    </ProcessContext.Provider>
  );
};

export const useProcess = () => {
  const context = useContext(ProcessContext);
  if (context === undefined) {
    throw new Error('useProcess must be used within a ProcessProvider');
  }
  return context;
};